import React, { createContext, useCallback, useContext, useEffect, useMemo } from "react";
import { useUserDisplayCurrency } from "../hooks/useUserDisplayCurrency";
import { formatDisplayCurrency } from "../utils/formatDisplayCurrency";
import { setSyncedDisplayCurrency } from "../utils/displayCurrencySync";

export type DisplayCurrencyContextValue = {
  displayCurrency: string;
  supportedCurrencies: string[];
  isLoading: boolean;
  revision: number;
  refetch: () => Promise<void>;
  formatMoney: (value: number, options?: { showSign?: boolean }) => string;
};

const defaultValue: DisplayCurrencyContextValue = {
  displayCurrency: "USD",
  supportedCurrencies: [],
  isLoading: false,
  revision: 0,
  refetch: async () => {},
  formatMoney: (value, options) =>
    formatDisplayCurrency(value, "USD", { locale: "en-US", ...options }),
};

const DisplayCurrencyContext = createContext<DisplayCurrencyContextValue>(defaultValue);

export function DisplayCurrencyProvider({ children }: { children: React.ReactNode }) {
  const { displayCurrency, supportedCurrencies, isLoading, revision, refetch } =
    useUserDisplayCurrency();

  useEffect(() => {
    setSyncedDisplayCurrency(displayCurrency);
  }, [displayCurrency]);

  const formatMoney = useCallback(
    (value: number, options?: { showSign?: boolean }) =>
      formatDisplayCurrency(value, displayCurrency, { locale: "en-US", ...options }),
    [displayCurrency]
  );

  const value = useMemo(
    () => ({
      displayCurrency,
      supportedCurrencies,
      isLoading,
      revision,
      refetch,
      formatMoney,
    }),
    [displayCurrency, supportedCurrencies, isLoading, revision, refetch, formatMoney]
  );

  return (
    <DisplayCurrencyContext.Provider value={value}>{children}</DisplayCurrencyContext.Provider>
  );
}

export function useDisplayCurrency(): DisplayCurrencyContextValue {
  return useContext(DisplayCurrencyContext);
}
